/**
 * Hook for tracking session file state
 *
 * Fetches initial file info via HTTP. Real-time file events (upload/remove)
 * are received through the transcript WebSocket and should be passed in
 * via the onFileUploaded/onFileRemoved callbacks on TranscriptPanel.
 */

import { useState, useEffect } from 'react'
import { resolveApiBaseUrl, type SessionFileInfo } from '@polygraf/shared'

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL)

interface UseSessionFileOptions {
	documentId: string
}

interface UseSessionFileReturn {
	file: SessionFileInfo | null
	isLoading: boolean
	downloadUrl: string
	setFile: (file: SessionFileInfo | null) => void
}

export function useSessionFile({ documentId }: UseSessionFileOptions): UseSessionFileReturn {
	const [file, setFile] = useState<SessionFileInfo | null>(null)
	const [isLoading, setIsLoading] = useState(true)

	const downloadUrl = documentId ? `${API_URL}/api/sessions/${documentId}/file` : ''

	useEffect(() => {
		if (!documentId) {
			setIsLoading(false)
			return
		}

		async function fetchFileInfo() {
			try {
				const res = await fetch(`${API_URL}/api/sessions/${documentId}/file/info`)
				if (res.ok) {
					const data = await res.json()
					setFile(data)
				} else {
					setFile(null)
				}
			} catch (err) {
				console.error('Failed to fetch session file info:', err)
				setFile(null)
			} finally {
				setIsLoading(false)
			}
		}

		fetchFileInfo()
	}, [documentId])

	return { file, isLoading, downloadUrl, setFile }
}
